import { typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

export interface SchemaFeature {
  name: string;
  dtype: "string" | "int" | "float" | "bool" | "date" | "datetime";
  required: boolean;
}

export interface ModelSchema {
  revision_no: number;
  features: SchemaFeature[];
  supports_proba: boolean;
  supports_confidence: boolean;
}

interface ApiSchemaResponse {
  model_id: string;
  task_type: string;
  label: string | null;
  feature_columns: Array<{
    name: string;
    dtype: string; // "int" | "float" | "string" | "bool" | "datetime" | "category"
    nullable: boolean;
  }>;
  revision_no?: number;
}

function normalizeDtype(dtype: string): SchemaFeature["dtype"] {
  switch (dtype) {
    case "int":
    case "float":
    case "string":
    case "bool":
    case "date":
    case "datetime":
      return dtype;
    case "category":
      return "string";
    default:
      return "string";
  }
}

export function useModelSchema(modelId: string | null) {
  return useQuery({
    enabled: !!modelId,
    queryKey: ["model-schema", modelId],
    queryFn: async (): Promise<ModelSchema> => {
      const { data, error } = await typedApi.GET("/v1/models/{model_id}/schema", {
        params: { path: { model_id: modelId ?? "" } },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: ModelSchemaResponse in openapi schema lacks revision_no field present at runtime
      const r = data as unknown as ApiSchemaResponse;
      return {
        revision_no: r.revision_no ?? 0,
        features: r.feature_columns.map((c) => ({
          name: c.name,
          dtype: normalizeDtype(c.dtype),
          required: !c.nullable,
        })),
        supports_proba: r.task_type === "tabular_classification",
        supports_confidence:
          r.task_type.startsWith("tabular_") || r.task_type === "anomaly_detection",
      };
    },
  });
}
